import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Quill } from 'react-quill';
import { FaCheck, FaEdit, FaExclamationTriangle, FaSave, FaSpinner, FaTimes } from 'react-icons/fa';
import trackService from '../../services/trackService';
import videoService from '../../services/videoService';
import contentService from '../../services/contentService';
import './EnhancedPageContentManager.css';
import './EnhancedPageContentManager.custom.css';
import { getCategoryFeatures } from './config/categoryFeatures';
import createSlug from './utils/createSlug';
import PostMetadataForm from './components/PostMetadataForm';
import CoverImageUploader from './components/CoverImageUploader';
import RichTextDescriptionEditor from './components/RichTextDescriptionEditor';
import AudioManager from './components/AudioManager';
import VideoManager from './components/VideoManager';
import MediaMetadataForm from './components/MediaMetadataForm';
import FullScreenLoader from '../common/FullScreenLoader';

const Font = Quill.import('formats/font');
Font.whitelist = ['sans-serif', 'serif', 'monospace', 'playfair', 'inter', 'roboto', 'merriweather'];
Quill.register(Font, true);

const INITIAL_POST_METADATA = {
    title: '',
    description: '',
    is_paid: false,
    price: 1,
    ai_score: 0,
    cover_image_url: '',
    slug: ''
};

const INITIAL_MEDIA_METADATA = {
    writer: '',
    composer: '',
    copyright: '',
    links: ''
};

const AUDIO_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/x-m4a', 'audio/mp4'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];

const EnhancedPostContentManager = ({
    postId,
    currentPost,
    onPostUpdate,
    onError,
    onSuccess,
    pagesMeData
}) => {
    const [isInitializing, setIsInitializing] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [postMetadata, setPostMetadata] = useState(INITIAL_POST_METADATA);
    const [mediaMetadata, setMediaMetadata] = useState(INITIAL_MEDIA_METADATA);
    const [isUploadingCover, setIsUploadingCover] = useState(false);
    const [coverImagePreview, setCoverImagePreview] = useState('');

    const [audioFile, setAudioFile] = useState(null);
    const [audioPreview, setAudioPreview] = useState('');
    const [audioSourceType, setAudioSourceType] = useState(null);
    const [isUploadingAudio, setIsUploadingAudio] = useState(false);
    const [isPlayingAudio, setIsPlayingAudio] = useState(false);
    const [audioDuration, setAudioDuration] = useState(0);
    const [audioCurrentTime, setAudioCurrentTime] = useState(0);
    const [audioFileName, setAudioFileName] = useState('');
    const [audioFileSize, setAudioFileSize] = useState(0);
    const [audioError, setAudioError] = useState('');

    const [videoFile, setVideoFile] = useState(null);
    const [videoPreview, setVideoPreview] = useState('');
    const [videoId, setVideoId] = useState(null);
    const [trackId, setTrackId] = useState(null);
    const [videoSourceType, setVideoSourceType] = useState(null);
    const [isUploadingVideo, setIsUploadingVideo] = useState(false);
    const [videoDuration, setVideoDuration] = useState(0);
    const [videoFileName, setVideoFileName] = useState('');
    const [videoFileSize, setVideoFileSize] = useState(0);
    const [videoError, setVideoError] = useState('');

    const quillRef = useRef(null);
    const audioRef = useRef(null);
    const videoRef = useRef(null);
    const successTimeoutRef = useRef(null);

    const categoryId = pagesMeData?.category_id ? Number(pagesMeData.category_id) : null;
    const features = useMemo(() => getCategoryFeatures(categoryId), [categoryId]);

    const showSuccess = useCallback((message) => {
        setSuccess(message);
        window.clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = window.setTimeout(() => setSuccess(''), 3000);
    }, []);

    const formatTime = useCallback((timeInSeconds) => {
        const safeTime = Number.isFinite(timeInSeconds) ? timeInSeconds : 0;
        const minutes = Math.floor(safeTime / 60);
        const seconds = Math.floor(safeTime % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    }, []);

    const handleMetadataChange = useCallback((field, value) => {
        setPostMetadata((previous) => {
            const nextMetadata = { ...previous, [field]: value };
            if (field === 'title') {
                nextMetadata.slug = createSlug(value);
            }
            return nextMetadata;
        });
        setHasUnsavedChanges(true);
    }, []);

    const handleMediaMetadataChange = useCallback((field, value) => {
        setMediaMetadata((previous) => ({ ...previous, [field]: value }));
        setHasUnsavedChanges(true);
    }, []);

    const compressImage = useCallback((file, maxSizeMB = 2) => new Promise((resolve, reject) => {
        const fileSizeMB = file.size / (1024 * 1024);
        const readAsDataUrl = (inputFile) => {
            const reader = new FileReader();
            reader.onload = (event) => resolve(event.target?.result || '');
            reader.onerror = () => reject(new Error('Failed to read image file.'));
            reader.readAsDataURL(inputFile);
        };

        if (fileSizeMB <= maxSizeMB) {
            readAsDataUrl(file);
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const compressionRatio = Math.sqrt(maxSizeMB / fileSizeMB);
                const width = Math.max(1, Math.floor(img.width * compressionRatio));
                const height = Math.max(1, Math.floor(img.height * compressionRatio));
                canvas.width = width;
                canvas.height = height;

                const context = canvas.getContext('2d');
                if (!context) {
                    reject(new Error('Failed to process image.'));
                    return;
                }

                context.drawImage(img, 0, 0, width, height);
                let quality = 0.9;
                let dataUrl = canvas.toDataURL('image/jpeg', quality);

                while (((dataUrl.length * 0.75) / (1024 * 1024)) > maxSizeMB && quality > 0.1) {
                    quality -= 0.1;
                    dataUrl = canvas.toDataURL('image/jpeg', quality);
                }

                resolve(dataUrl);
            };
            img.onerror = () => reject(new Error('Failed to load image.'));
            img.src = String(event.target?.result || '');
        };
        reader.onerror = () => reject(new Error('Failed to read image file.'));
        reader.readAsDataURL(file);
    }), []);

    const imageHandler = useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const [file] = input.files || [];
            if (!file) {
                return;
            }

            const quill = quillRef.current?.getEditor();
            if (!quill) {
                return;
            }

            const range = quill.getSelection(true);
            quill.insertText(range.index, 'Uploading image...');

            try {
                const compressedImage = await compressImage(file, 2);
                quill.deleteText(range.index, 'Uploading image...'.length);
                quill.insertEmbed(range.index, 'image', compressedImage);
                quill.setSelection(range.index + 1);
                setHasUnsavedChanges(true);
            } catch (uploadError) {
                quill.deleteText(range.index, 'Uploading image...'.length);
                setError(uploadError.message || 'Failed to process image. Please try again.');
            }
        };
    }, [compressImage]);

    const quillModules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                [{ font: ['sans-serif', 'serif', 'monospace', 'inter', 'roboto', 'merriweather', 'playfair'] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link', 'image', 'video'],
                ['blockquote'],
                ['clean']
            ],
            handlers: { image: imageHandler }
        },
        clipboard: { matchVisual: false }
    }), [imageHandler]);

    const quillFormats = useMemo(() => ([
        'header', 'font', 'bold', 'italic', 'underline', 'strike',
        'list', 'bullet', 'link', 'image', 'video', 'blockquote'
    ]), []);

    useEffect(() => {
        return () => {
            if (audioSourceType === 'object' && audioPreview) {
                URL.revokeObjectURL(audioPreview);
            }
            if (videoSourceType === 'object' && videoPreview) {
                URL.revokeObjectURL(videoPreview);
            }
            window.clearTimeout(successTimeoutRef.current);
        };
    }, [audioPreview, audioSourceType, videoPreview, videoSourceType]);

    // 1. Dedicated Cleanup Effect: ONLY runs when the component unmounts
    // This prevents the "disappearing preview" because it won't run when state changes
    useEffect(() => {
        return () => {
            if (audioSourceType === 'object' && audioPreview) {
                URL.revokeObjectURL(audioPreview);
            }
            if (videoSourceType === 'object' && videoPreview) {
                URL.revokeObjectURL(videoPreview);
            }
        };
    }, [audioPreview, audioSourceType, videoPreview, videoSourceType]);

    // 2. Data Initialization Effect: ONLY runs when the Post ID or Post Data changes
    useEffect(() => {
        let isMounted = true;

        const initialize = async () => {
            if (!currentPost || !postId) {
                setIsInitializing(false);
                return;
            }

            setIsInitializing(true);
            setError('');

            // Reset local states for the new post
            const nextPostMetadata = {
                title: currentPost.title || '',
                description: currentPost.description || '',
                is_paid: Boolean(currentPost.is_paid),
                price: currentPost.price || 1,
                ai_score: currentPost.ai_score || 0,
                cover_image_url: currentPost.cover_image_url || '',
                slug: currentPost.slug || createSlug(currentPost.title || '')
            };

            const nextMediaMetadata = {
                writer: currentPost.writer || currentPost.track?.writer || currentPost.video?.writer || '',
                composer: currentPost.composer_name || currentPost.track?.composer_name || currentPost.video?.composer_name || '',
                copyright: currentPost.copyright || currentPost.track?.copyright || currentPost.video?.copyright || '',
                links: currentPost.links || currentPost.track?.links || currentPost.video?.links || ''
            };

            if (!isMounted) return;

            setPostMetadata(nextPostMetadata);
            setMediaMetadata(nextMediaMetadata);
            setCoverImagePreview(currentPost.cover_image_url || '');
            setHasUnsavedChanges(false);

            // Clear previous file selections
            setAudioFile(null);
            setVideoFile(null);
            setAudioPreview('');
            setVideoPreview('');

            try {
                if (features.supportsAudio && currentPost.track?.file_size>0) {
                    const currentTrack = await trackService.getTrackStreamUrlForPosts(currentPost.track?.id || trackId);
                    if (currentTrack && isMounted) {
                        setAudioPreview(currentTrack?.stream_url);
                        setAudioSourceType('remote');
                    }
                }

                if (features.supportsVideo) {
                    const existingVideo = await videoService.getVideoDetailsByPostId(postId);
                    if (existingVideo && isMounted) {
                        const streamData = await videoService.getVideoStreamUrlForPosts(existingVideo.id);
                        if (streamData?.stream_url && isMounted) {
                            setVideoId(existingVideo.id);
                            setVideoPreview(streamData.stream_url);
                            setVideoSourceType('remote');
                        }
                    }
                }
            } catch (loadError) {
                if (isMounted) setError('Failed to load existing media.');
            } finally {
                if (isMounted) setIsInitializing(false);
            }
        };

        initialize();

        return () => {
            isMounted = false;
        };
        // REMOVE audioPreview, videoPreview, etc. from here!
        // This effect should only re-run if the actual Post record changes.
    }, [currentPost, postId, features.supportsAudio, features.supportsVideo, trackId]);

    const handleCoverImageUpload = useCallback(async (file) => {
        setIsUploadingCover(true);
        setError('');
        try {
            const compressedImage = await compressImage(file, 2);
            const response = await fetch(compressedImage);
            const blob = await response.blob();
            const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
            });
            const data = await contentService.uploadImage(compressedFile);
            handleMetadataChange('cover_image_url', data.file_url);
            setCoverImagePreview(data.file_url);
            showSuccess(`Cover image uploaded successfully (${(data.file_size / 1024).toFixed(2)} KB)`);
        } catch (uploadError) {
            setError(uploadError.message || 'Failed to upload cover image. Please try again.');
        } finally {
            setIsUploadingCover(false);
        }
    }, [compressImage, handleMetadataChange, showSuccess]);

    const handleRemoveCoverImage = useCallback(() => {
        handleMetadataChange('cover_image_url', '');
        setCoverImagePreview('');
    }, [handleMetadataChange]);

    const handleAudioUpload = useCallback(async (file) => {
        setIsUploadingAudio(true);
        setAudioError('');
        try {
            if (!AUDIO_TYPES.includes(file.type)) {
                throw new Error('Invalid file type. Please upload MP3, WAV, OGG, or M4A audio files.');
            }
            if (audioSourceType === 'object' && audioPreview) {
                URL.revokeObjectURL(audioPreview);
            }
            const preview = URL.createObjectURL(file);
            setAudioFile(file);
            setAudioPreview(preview);
            setAudioSourceType('object');
            setAudioFileName(file.name);
            setAudioFileSize(file.size);
            setAudioDuration(0);
            setAudioCurrentTime(0);
            setIsPlayingAudio(false);
            setHasUnsavedChanges(true);
            showSuccess(`Audio file uploaded successfully (${(file.size / (1024 * 1024)).toFixed(2)} MB)`);
        } catch (uploadError) {
            setAudioError(uploadError.message || 'Failed to upload audio file. Please try again.');
        } finally {
            setIsUploadingAudio(false);
        }
    }, [audioPreview, audioSourceType, showSuccess]);

    const handleRemoveAudio = useCallback(() => {
        if (currentPost.track || trackId) {
            trackService.deleteTrack(trackId || currentPost.track?.id)
                .then(r => console.log('track delete response', r))
                .catch(e => console.error('track delete error', e));
        }
        if (audioSourceType === 'object' && audioPreview) {
            URL.revokeObjectURL(audioPreview);
        }
        if (audioRef.current) {
            audioRef.current.pause();
        }
        setAudioFile(null);
        setAudioPreview('');
        setAudioSourceType(null);
        setAudioFileName('');
        setAudioFileSize(0);
        setAudioDuration(0);
        setAudioCurrentTime(0);
        setIsPlayingAudio(false);
        setHasUnsavedChanges(true);
    }, [audioPreview, audioSourceType, trackId, currentPost.track]);

    const handleToggleAudioPlayback = useCallback((forcedState) => {
        if (!audioRef.current) {
            return;
        }

        const shouldPlay = typeof forcedState === 'boolean' ? forcedState : !isPlayingAudio;
        if (shouldPlay) {
            audioRef.current.play().catch(() => {
                setAudioError('Unable to play audio preview.');
            });
        } else {
            audioRef.current.pause();
        }
        setIsPlayingAudio(shouldPlay);
    }, [isPlayingAudio]);

    const handleVideoUpload = useCallback(async (file) => {
        setIsUploadingVideo(true);
        setVideoError('');
        try {
            if (!VIDEO_TYPES.includes(file.type)) {
                throw new Error('Invalid file type. Please upload MP4, WebM, MOV, or AVI files.');
            }
            if (videoSourceType === 'object' && videoPreview) {
                URL.revokeObjectURL(videoPreview);
            }
            const preview = URL.createObjectURL(file);
            setVideoFile(file);
            setVideoPreview(preview);
            setVideoSourceType('object');
            setVideoFileName(file.name);
            setVideoFileSize(file.size);
            setVideoDuration(0);
            setHasUnsavedChanges(true);
            showSuccess(`Video selected (${(file.size / (1024 * 1024)).toFixed(2)} MB) — save to upload`);
        } catch (uploadError) {
            setVideoError(uploadError.message || 'Failed to upload video file. Please try again.');
        } finally {
            setIsUploadingVideo(false);
        }
    }, [showSuccess, videoPreview, videoSourceType]);

    const handleRemoveVideo = useCallback(() => {
        if (videoId) {
            videoService.deleteVideo(videoId)
                .then(r => console.log('video delete response', r))
                .catch(e => console.error('video delete error', e));
        }
        if (videoSourceType === 'object' && videoPreview) {
            URL.revokeObjectURL(videoPreview);
        }
        setVideoFile(null);
        setVideoPreview('');
        setVideoSourceType(null);
        setVideoFileName('');
        setVideoFileSize(0);
        setVideoDuration(0);
        setHasUnsavedChanges(true);
    }, [videoPreview, videoSourceType, videoId]);

    const buildMediaPayload = useCallback((metadata) => ({
        title: metadata.title,
        description: metadata.description,
        cover_image_url: metadata.cover_image_url,
        artist: mediaMetadata.composer || mediaMetadata.writer || '',
        writer: mediaMetadata.writer,
        composer_name: mediaMetadata.composer,
        copyright: mediaMetadata.copyright,
        links: mediaMetadata.links
    }), [mediaMetadata]);

    const syncAudio = useCallback(async (normalizedMetadata) => {
        const payload = {
            ...buildMediaPayload(normalizedMetadata),
            post_id: postId,
            audio_url: audioFile ? undefined : (audioPreview || normalizedMetadata.description || '')
        };

        if (audioFile) {
            const result = await trackService.syncTrack({ ...payload, audio_file: audioFile });
            setTrackId(result?.id);
            if (!result) {
                throw new Error(result.error || 'Failed to sync track.');
            }
            return;
        }
        const result = await trackService.updateTrackMetadata(postId, payload);
        if (!result) {
            throw new Error(result.error || 'Failed to update track metadata.');
        }
    }, [audioFile, audioPreview, buildMediaPayload, postId]);

    const syncVideo = useCallback(async (normalizedMetadata) => {
        const payload = {
            ...buildMediaPayload(normalizedMetadata),
            post_id: postId
        };

        if (videoFile) {
            const result = await videoService.syncVideo({ ...payload, video_file: videoFile });
            if (!result.success) {
                const err = new Error(result.error || 'Failed to sync video.');
                err.isVideoError = true;
                throw err;
            }
            return;
        }

        const result = await videoService.updateVideoMetadata(postId, payload);
        if (!result.success) {
            const err = new Error(result.error || 'Failed to update video metadata.');
            err.isVideoError = true;
            throw err;
        }
    }, [buildMediaPayload, postId, videoFile]);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setError('');
        setSuccess('');
        setVideoError('');

        try {
            const normalizedMetadata = {
                ...postMetadata,
                slug: postMetadata.slug || createSlug(postMetadata.title),
                is_paid: postMetadata.is_paid === true,
                price: postMetadata.is_paid ? postMetadata.price : 0,
                cover_image_url: postMetadata.cover_image_url || ''
            };

            const updatedPost = await (await import('../../services/postService')).default.updatePost(postId, normalizedMetadata);
            if (onPostUpdate) {
                onPostUpdate(updatedPost);
            }

            const mediaErrors = [];
            let hasVideoError = false;

            if (features.supportsAudio) {
                try {
                    await syncAudio(normalizedMetadata);
                } catch (mediaError) {
                    mediaErrors.push(mediaError.message);
                }
            }

            if (features.supportsVideo) {
                try {
                    await syncVideo(normalizedMetadata);
                } catch (mediaError) {
                    if (mediaError.isVideoError) {
                        setVideoError(mediaError.message);
                        hasVideoError = true;
                    } else {
                        mediaErrors.push(mediaError.message);
                    }
                }
            }

            if (mediaErrors.length > 0 || hasVideoError) {
                if (mediaErrors.length > 0) {
                    const partialMessage = `Post saved, but media sync failed: ${mediaErrors.join(' ')}`;
                    setError(partialMessage);
                    if (onError) onError(partialMessage);
                }
                setIsSaving(false);
                return;
            }

            setHasUnsavedChanges(false);

            // Show success animation
            setShowSaveSuccess(true);

            setTimeout(() => {
                setShowSaveSuccess(false);
                setIsSaving(false);
                showSuccess('Post details updated successfully');
                if (onSuccess) {
                    onSuccess('Post details updated successfully');
                }
            }, 1200);
        } catch (saveError) {
            const message = saveError.message || 'Failed to save post details.';
            setError(message);
            if (onError) {
                onError(message);
            }
            setIsSaving(false);
        }
    }, [features.supportsAudio, features.supportsVideo, onError, onPostUpdate, onSuccess, postId, postMetadata, showSuccess, syncAudio, syncVideo]);

    if (isInitializing) {
        return (
            <div className="card p-5 text-center">
                <div className="d-flex flex-column align-items-center">
                    <FaSpinner className="spinner mb-3" style={{ fontSize: '2rem', color: 'var(--primary)' }} />
                    <p className="h5 text-muted">Loading content...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="enhanced-content-manager">
            <div className="card mb-4">
                <div className="card-header">
                    <h3 className="card-title h5 m-0 d-flex align-items-center">
                        <FaEdit className="me-2" /> Post Details
                    </h3>
                </div>
                <div className="card-body">
                    <div className="metadata-form">
                        <PostMetadataForm metadata={postMetadata} onChange={handleMetadataChange} />

                        <CoverImageUploader
                            previewUrl={coverImagePreview}
                            isUploading={isUploadingCover}
                            onUpload={handleCoverImageUpload}
                            onRemove={handleRemoveCoverImage}
                        />

                        {features.supportsAudio && (
                            <AudioManager
                                description={postMetadata.description}
                                onDescriptionChange={(value) => handleMetadataChange('description', value)}
                                audioError={audioError}
                                audioPreview={audioPreview}
                                audioRef={audioRef}
                                isPlaying={isPlayingAudio}
                                audioCurrentTime={audioCurrentTime}
                                audioDuration={audioDuration}
                                audioFileName={audioFileName}
                                audioFileSize={audioFileSize}
                                isUploadingAudio={isUploadingAudio}
                                onTogglePlayback={handleToggleAudioPlayback}
                                onRemoveAudio={handleRemoveAudio}
                                onUploadAudio={handleAudioUpload}
                                formatTime={formatTime}
                            />
                        )}

                        {features.supportsVideo && (
                            <VideoManager
                                description={postMetadata.description}
                                onDescriptionChange={(value) => handleMetadataChange('description', value)}
                                videoError={videoError}
                                videoPreview={videoPreview}
                                videoRef={videoRef}
                                videoDuration={videoDuration}
                                videoFileName={videoFileName}
                                videoFileSize={videoFileSize}
                                isUploadingVideo={isUploadingVideo}
                                onRemoveVideo={handleRemoveVideo}
                                onUploadVideo={handleVideoUpload}
                                formatTime={formatTime}
                                onLoadedMetadata={() => setVideoDuration(videoRef.current?.duration || 0)}
                            />
                        )}

                        {features.supportsRichText && (
                            <RichTextDescriptionEditor
                                quillRef={quillRef}
                                value={postMetadata.description}
                                onChange={(value) => handleMetadataChange('description', value)}
                                modules={quillModules}
                                formats={quillFormats}
                            />
                        )}

                        {(features.supportsAudio || features.supportsVideo) && (
                            <MediaMetadataForm mediaMetadata={mediaMetadata} onChange={handleMediaMetadataChange} />
                        )}

                        {error && (
                            <div className="alert alert-danger">
                                <FaTimes className="me-2" />
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="alert alert-success">
                                <FaCheck className="me-2" />
                                {success}
                            </div>
                        )}

                        <div className="d-flex align-items-center gap-3 mt-4">
                            <button
                                onClick={handleSave}
                                disabled={isSaving || !hasUnsavedChanges}
                                className={`btn btn-primary ${!hasUnsavedChanges ? 'disabled' : ''}`}
                            >
                                <FaSave className="me-2" />
                                {features.saveLabel}
                            </button>

                            {hasUnsavedChanges && (
                                <div className="d-flex align-items-center text-warning">
                                    <FaExclamationTriangle className="me-1" />
                                    <span className="fw-medium">Unsaved changes</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <audio
                ref={audioRef}
                src={audioPreview}
                onTimeUpdate={() => setAudioCurrentTime(audioRef.current?.currentTime || 0)}
                onLoadedMetadata={() => setAudioDuration(audioRef.current?.duration || 0)}
                onEnded={() => setIsPlayingAudio(false)}
                style={{ display: 'none' }}
            />

            <FullScreenLoader 
                isVisible={isSaving && !showSaveSuccess} 
                message="Saving your changes..."
                subtext="Please wait, this will take a moment"
                showSuccess={showSaveSuccess}
            />
        </div>
    );
};

export default EnhancedPostContentManager;
